import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect, useMemo } from "react"

import moveLocalStorageKey from "@utils/moveLocalStorageKey"

import { TableViewConfig } from "@organisms/ObjectsView/TableView/TableView.types"
import { migrateConfig } from "@organisms/ObjectsView/TableView/TableView.utils"

import defaultTableViewConfig, { TableViewConfigType } from "@constants/defaultTableViewConfig"

const TABLE_VIEW_CONFIG_STORAGE_KEY = "roopairs_table_view_config"

// The table config was stored as "objectsView" from before the cards view had a store of its own.
moveLocalStorageKey("objectsView", TABLE_VIEW_CONFIG_STORAGE_KEY)

const tableViewConfigAtom = atomWithStorage<TableViewConfigType>(
    TABLE_VIEW_CONFIG_STORAGE_KEY,
    defaultTableViewConfig,
    undefined,
    {
        getOnInit: true,
    },
)

export default function useTableViewConfig(tableStorageKey: string, defaultTableConfigOverride?: TableViewConfig) {
    const [currentTableViewConfig, setTableViewConfig] = useAtom(tableViewConfigAtom)

    const defaultTableConfig = defaultTableConfigOverride || defaultTableViewConfig.table[tableStorageKey]

    const persistedTableConfig = currentTableViewConfig.table[tableStorageKey]

    const effectiveTableConfig = useMemo(() => {
        if (!persistedTableConfig) {
            return defaultTableConfig
        }

        // A persisted entry can predate newly-shaped defaults (or have been written from a partial
        // update), so the defaults always back-fill any keys the stored config is missing.
        return { ...defaultTableConfig, ...persistedTableConfig }
    }, [persistedTableConfig, defaultTableConfig])

    useEffect(() => {
        if (!currentTableViewConfig || !defaultTableViewConfig) {
            return
        }

        const hasChanges = currentTableViewConfig.version !== defaultTableViewConfig.version

        if (hasChanges) {
            const migratedConfig = migrateConfig(currentTableViewConfig, defaultTableViewConfig)
            setTableViewConfig(migratedConfig)
        }
    }, [])

    const updateCurrentTableViewConfig = (
        key: keyof TableViewConfig,
        updatedValue: TableViewConfig[keyof TableViewConfig],
    ) => {
        setTableViewConfig((prevViews) => ({
            ...prevViews,
            table: {
                ...prevViews.table,
                [tableStorageKey]: {
                    // Tables configured through defaultTableConfigOverride have no entry in the
                    // global defaults, so seeding from the override keeps the first persisted
                    // write complete instead of storing only the updated key.
                    ...defaultTableConfig,
                    ...(prevViews.table[tableStorageKey] as TableViewConfig),
                    [key]: updatedValue,
                },
            },
        }))
    }

    // Read another table's entry as persisted, without this table's defaults or override.
    const getTableViewConfig = (storageKey: string) => currentTableViewConfig.table[storageKey]

    return {
        currentTableViewConfig: effectiveTableConfig,
        updateCurrentTableViewConfig,
        getTableViewConfig,
    }
}
